import { PROFILE_LABELS } from '@/shared/session/profiles';

import type { UserHistoryEntry } from './types';

/**
 * One line of the history of a person (contract v10.58) as a sentence: «Deja de ver
 * ventas@…», «Perfil: de Tienda a Comercial». Who did it and when go apart, in the
 * line below; this is the what.
 */
export function historySentence(entry: UserHistoryEntry): string {
  const parts: string[] = [];
  switch (entry.event) {
    case 'user_created': {
      parts.push(
        entry.via === 'invite'
          ? 'Alta con un enlace de invitación'
          : 'Alta con una contraseña provisional',
      );
      if (entry.mailboxes_added?.length) parts.push(`buzones: ${entry.mailboxes_added.join(', ')}`);
      if (entry.profile_to) parts.push(`perfil ${PROFILE_LABELS[entry.profile_to]}`);
      if (entry.is_admin) parts.push('administra');
      return parts.join(' · ');
    }
    case 'role_changed': {
      for (const each of entry.mailboxes_added ?? []) parts.push(`Pasa a ver ${each}`);
      for (const each of entry.mailboxes_removed ?? []) parts.push(`Deja de ver ${each}`);
      if (entry.is_admin === true) parts.push('Pasa a administrar');
      if (entry.is_admin === false) parts.push('Deja de administrar');
      if (entry.is_superadmin === true) parts.push('Pasa a superadministrador');
      if (entry.is_superadmin === false) parts.push('Deja de ser superadministrador');
      return parts.join(' · ') || 'Cambio de acceso';
    }
    case 'profile_changed':
      return entry.profile_from && entry.profile_to
        ? `Perfil: de ${PROFILE_LABELS[entry.profile_from]} a ${PROFILE_LABELS[entry.profile_to]}`
        : 'Cambio de perfil';
    case 'payroll_permission_changed':
      return entry.can_send_payroll
        ? 'Pasa a ver y enviar las nóminas'
        : 'Deja de ver y enviar las nóminas';
    case 'user_disabled':
      return 'Cuenta desactivada: deja de poder entrar';
    case 'user_enabled':
      return 'Cuenta reactivada';
    case 'password_reset':
      if (entry.via === 'invite') return 'Enlace de invitación nuevo';
      if (entry.via === 'forced') return 'Se le pide cambiar la contraseña al entrar';
      return 'Contraseña provisional nueva';
    case 'two_factor_reset':
      return entry.via === 'command'
        ? 'Doble factor reiniciado desde la terminal'
        : 'Doble factor reiniciado';
    case 'password_changed':
      return 'Cambió su contraseña';
    case 'two_factor_enabled':
      return 'Activó el doble factor';
    default:
      return entry.event;
  }
}

/** Whether the line takes something away, for its tone. */
export function historyNarrows(entry: UserHistoryEntry): boolean {
  return (
    entry.event === 'user_disabled' ||
    (entry.mailboxes_removed?.length ?? 0) > 0 ||
    entry.is_admin === false ||
    entry.can_send_payroll === false
  );
}
